import Dialog from "@components/dialog/Dialog";
import { isVisibleByPath } from "@components/smart/FieldInfo";
import { isAddressEmpty } from "@components/smart/GeneralFieldDefinition";
import { getCollapsedGroupId } from "@components/smart/Smart.utils";
import { ISmartFieldChange } from "@components/smart/smartField/SmartField";
import { ISummaryItemProps } from "@components/summary/SummaryItem";
import BindingContext, { createPath, IEntity } from "@odata/BindingContext";
import { ICachedFieldState, setBoundValue } from "@odata/Data.utils";
import {
    AddressEntity,
    IBankAccountEntity,
    ICountryEntity,
    IPrEmploymentEntity,
    IUserEntity,
    PrEmployeeCzechAddressEntity,
    PrEmployeeEntity,
    PrEmployeePermanentAddressEntity,
    PrEmployeeTemporalCurrentEntity,
    PrEmployeeTemporalEntity,
    PrLaborMarketAccessEntity
} from "@odata/GeneratedEntityTypes";
import {
    CountryCode,
    CurrencyCode,
    MpsvFreeAccessToLaborMarketTypeCode,
    PrIdentityDocumentTypeCode,
    PrPensionTypeCode,
    PrSubmissionSubtypeCode,
    PrSubmissionTypeCode
} from "@odata/GeneratedEnums";
import {
    getCurrentRangeIndex,
    getGroupingProperties,
    groupAndOptimizeRanges,
    handleCurrentTemporalPropChange,
    setTemporalCurrentValue,
    TTemporal,
    ungroupTemporalRanges
} from "@odata/TemporalUtils";
import DialogPage from "@pages/DialogPage";
import {
    applyPensionDiscountPath,
    EMPLOYEE_USER_TAB_ID,
    employeeSummaryLocalPath,
    hasCzechResidency,
    isForeigner
} from "@pages/payroll/employee/Employee.def";
import SentToEmployeeFormView from "@pages/payroll/employee/SendToEmployeeFormView";
import SubmissionWizard from "@pages/payroll/submission/submissionWizard/SubmissionWizard";
import { getDefinitions as getTaxpayerDeclarationDefinitions } from "@pages/payroll/taxpayerDeclaration/TaxpayerDeclaration.def";
import TaxpayerDeclarationTableView from "@pages/payroll/taxpayerDeclaration/TaxpayerDeclarationTableView";
import { isDefined, isNotDefined } from "@utils/general";
import { memoizeOne } from "@utils/memoizeOne";
import { Dayjs } from "dayjs";
import i18next from "i18next";
import { cloneDeep, isEmpty } from "lodash";
import React from "react";

import { AppContext } from "../../../contexts/appContext/AppContext.types";
import { withPermissionContext } from "../../../contexts/permissionContext/withPermissionContext";
import { FormMode, Status } from "../../../enums";
import { TRecordAny } from "../../../global.types";
import { getUtcDayjs } from "../../../types/Date";
import { FormStorage, IContextInitArgs } from "../../../views/formView/FormStorage";
import { FormViewForExtend, IFormViewProps } from "../../../views/formView/FormView";
import PlugAndPlayForm from "../../../views/formView/PlugAndPlayForm";
import {
    handleBankFieldChange,
    SAVED_ACCOUNTS_PATH,
    setSavedBankAccount,
    setSavedBankAccountItems
} from "../../banks/bankAccounts/BankAccounts.utils";
import { loadAndStoreRanges, setMatchingNumberRange } from "../../numberRange/NumberRange.utils";
import {
    getEmployeeEmployments,
    IEmployeeFormCustomData,
    IPrEmployeeEntityExtended,
    OtherIdentifierFields,
    PreviousEmploymentDurationDaysPath,
    PreviousEmploymentDurationYearsPath,
    SEND_EMPLOYEE_INVITATION_ACTION,
    SEND_PAYSLIP_PASSWORD_ACTION,
    SendToEmployeeActionType,
    ShowContactAddressPath,
    ShowCzechAddressPath,
    ShowDisabilityPath,
    ShowForeignInsuranceCarrierPath,
    ShowOtherIdentifiers,
    ShowPensionPath,
    ShowSocialInsuranceDiscountLocalPath,
    ShowStudyPath,
    TAX_PAYER_DECLARATION_ACTION
} from "./Employee.utils";
import { getDefinitions as sendInvitationDef, sendToEmployeeMailPath } from "./SendToEmployee.def";

interface IProps extends IFormViewProps<IPrEmployeeEntityExtended, IEmployeeFormCustomData> {
}

class EmployeeFormView extends FormViewForExtend<IPrEmployeeEntityExtended, IProps> {
    static contextType = AppContext;

    static defaultProps = {
        title: "Employee:FormTitle"
    };

    constructor(props: IProps) {
        super(props);

        this.handleSmartTemporalPropertyDialogChange.bind(this);
    }

    getAdditionalLoadPromise(args: IContextInitArgs): (Promise<unknown | void>)[] {
        return [...super.getAdditionalLoadPromise(args), getEmployeeEmployments(args.bindingContext.getKey(), this.props.storage)];
    }

    async onAfterLoad() {
        const { storage } = this.props;
        const isAuditTrail = storage.formMode === FormMode.AuditTrail;

        if (!isAuditTrail) {
            const promises: Promise<unknown>[] = [];

            if (!storage.data.entity.NumberOurs) {
                promises.push(setMatchingNumberRange(storage.getThis(), true));
            } else {
                promises.push(loadAndStoreRanges(storage));
            }

            await Promise.all(promises);
        }

        if (storage.data.bindingContext.isNew()) {
            storage.setGroupStatus({
                isExpanded: true
            }, getCollapsedGroupId(SAVED_ACCOUNTS_PATH));
        }

        this.setupBirthNumberProperLabel();
        this.prepareBankAccount();
        this.setSpecialGroupsVisibility();
        this.preparePreviousEmploymentDurationValues();
        this.preparePensionTemporalPropertyBag();
        this.defaultDateResidencyFrom();

        return super.onAfterLoad();
    }

    async handleCustomHeaderAction(actionId: string): Promise<void> {
        switch (actionId) {
            case TAX_PAYER_DECLARATION_ACTION:
                this.props.storage.setCustomData({ taxPayerDeclarationDialogOpen: true });
                this.forceUpdate();
                break;
            case PrSubmissionTypeCode.EmployerDiscountOnSocialInsurance:
            case PrSubmissionTypeCode.EmployersMassHealthInsuranceNotification:
                this.props.storage.setCustomData({ submissionType: actionId });
                this.forceUpdate();
                break;
            case SEND_EMPLOYEE_INVITATION_ACTION:
                this.props.storage.setCustomData({ sendEmployeeDialogType: SendToEmployeeActionType.SendEmployeeInvitation });
                this.forceUpdate();
                break;
            case SEND_PAYSLIP_PASSWORD_ACTION:
                this.props.storage.setCustomData({ sendEmployeeDialogType: SendToEmployeeActionType.SendPayslipPassword });
                this.forceUpdate();
                break;
            default:
                return super.handleCustomHeaderAction(actionId);
        }
    }

    prepareBankAccount = (): void => {
        const accounts: IBankAccountEntity[] = [];
        const currentAccount: IBankAccountEntity = this.entity.BankAccount ? cloneDeep(this.entity.BankAccount) : null;

        if (currentAccount?.AccountNumber) {
            accounts.push(currentAccount);
        }

        const storage = this.props.storage.getThis();

        if (accounts.length > 0) {
            setSavedBankAccountItems(storage, accounts);
        }

        setSavedBankAccount({
            storage,
            isEntityWithoutBP: true,
            isInit: true,
            account: currentAccount
        }, accounts);
    };
    /** Several form groups has yes/no visibility switch. Set its default state based on the current form data. */
    setSpecialGroupsVisibility = (): void => {
        const { storage } = this.props;
        storage.setValueByPath(ShowContactAddressPath, !!this.entity.ContactAddress?.Street);
        storage.setValueByPath(ShowCzechAddressPath, !!this.entity.CzechAddress?.City);
        storage.setValueByPath(ShowForeignInsuranceCarrierPath, !!this.entity.ForeignInsuranceCarrier?.Name);
        storage.setValueByPath(ShowDisabilityPath, !!this.entity.TemporalPropertyBag?.find(item => item?.PrDisabilityTypeCode));
        storage.setValueByPath(ShowPensionPath, !!this.entity.CurrentTemporalPropertyBag?.DatePensionFrom);
        storage.setValueByPath(ShowSocialInsuranceDiscountLocalPath, !!this.entity.CurrentTemporalPropertyBag?.PrSocialInsuranceDiscountType?.Code);
        storage.setValueByPath(ShowStudyPath, !!this.entity.CurrentTemporalPropertyBag?.PrFormOfStudyTypeCode);
        const hasOtherIdentifiers = isForeigner({ storage }) && OtherIdentifierFields.some(field => {
            const value = storage.getValueByPath(field);
            return !isEmpty(value);
        });
        storage.setValueByPath(ShowOtherIdentifiers, hasOtherIdentifiers);
    };

    preparePreviousEmploymentDurationValues = (): void => {
        const sumDays = this.props.storage.getValueByPath(PrEmployeeEntity.PreviousEmploymentDurationDays);

        if (isNotDefined(sumDays)) {
            return;
        }

        const years = Math.floor(sumDays / 365);
        const days = sumDays % 365;

        this.props.storage.setValueByPath(PreviousEmploymentDurationYearsPath, years);
        this.props.storage.setValueByPath(PreviousEmploymentDurationDaysPath, days);
    };

    defaultDateResidencyFrom = (): void => {
        const { storage } = this.props;
        let dateEmploymentStart: Dayjs;
        const { employments } = storage.getCustomData();
        employments?.forEach((employment: IPrEmploymentEntity) => {
            if (!dateEmploymentStart || dateEmploymentStart.isAfter(employment.DateStart)) {
                dateEmploymentStart = getUtcDayjs(employment.DateStart);
            }
        });
        // default date according to https://solitea-cz.atlassian.net/browse/DEV-44275
        const minDate = getUtcDayjs("2026-01-01");
        const dateResidencyFrom = dateEmploymentStart && dateEmploymentStart.isAfter(minDate) ? dateEmploymentStart : minDate;
        storage.setValueByPath(PrEmployeeEntity.DateResidencyFrom, dateResidencyFrom);
    }

    preparePensionTemporalPropertyBag = (): void => {
        this.entity.TemporalPropertyBag?.forEach((item: TRecordAny) => {
            item[applyPensionDiscountPath] = isDefined(item.DateWorkingRetireeSocialInsuranceDiscountFrom);
        });
        (this.entity.CurrentTemporalPropertyBag as TRecordAny)[applyPensionDiscountPath] = isDefined(this.entity.CurrentTemporalPropertyBag.DateWorkingRetireeSocialInsuranceDiscountFrom);
    };

    handleCitizenshipChange = (e: ISmartFieldChange): void => {
        const { storage } = this.props;
        const path = e.bindingContext.getPath();
        if (path === PrEmployeeEntity.Citizenship) {
            this.setupBirthNumberProperLabel();

            // https://solitea-cz.atlassian.net/browse/DEV-36389
            const isCzech = e.value === CountryCode.CzechRepublic;
            storage.setValueByPath(PrEmployeeEntity.PrIdentificationDocument, isCzech ? PrIdentityDocumentTypeCode.IDCard : PrIdentityDocumentTypeCode.Passport);
            storage.setValueByPath(PrEmployeeEntity.IDCountryOfIssue, e.additionalData);

            // https://solitea-cz.atlassian.net/browse/DEV-44018
            // https://solitea-cz.atlassian.net/browse/DEV-44209
            if (!isCzech) {
                const { Citizenship } = storage.data.entity;
                const hasFreeAccess = !!Citizenship?.IsEEA || Citizenship?.Code === CountryCode.Switzerland;
                storage.setValueByPath(createPath(PrEmployeeEntity.LaborMarketAccess, PrLaborMarketAccessEntity.HasFreeAccessToLaborMarket), hasFreeAccess);
                storage.setValueByPath(createPath(PrEmployeeEntity.LaborMarketAccess, PrLaborMarketAccessEntity.FreeAccessType), hasFreeAccess ? MpsvFreeAccessToLaborMarketTypeCode.EuCitizen : null);
                storage.addActiveGroupByKey("accessToLaborMarket");
            }

            const origCountry = e.previousValue as ICountryEntity;
            const countryOfResidence = storage.getValueByPath(PrEmployeeEntity.CountryOfResidency, { useDirectValue: false });
            if (origCountry?.Code === countryOfResidence) {
                // propagate change as there are additional cascade effects
                this.handleChange({
                    bindingContext: storage.data.bindingContext.navigate(PrEmployeeEntity.CountryOfResidency),
                    value: e.value,
                    additionalData: e.additionalData,
                    triggerAdditionalTasks: true
                });
            }
            const birthCountry = storage.getValueByPath(PrEmployeeEntity.CountryOfBirth, { useDirectValue: false });
            if (origCountry?.Code === birthCountry) {
                storage.setValueByPath(PrEmployeeEntity.CountryOfBirth, e.value);
            }

            this.props.storage.refresh();
        }
        if (path === PrLaborMarketAccessEntity.HasFreeAccessToLaborMarket && !e.value) {
            storage.setValueByPath(createPath(PrEmployeeEntity.LaborMarketAccess, PrLaborMarketAccessEntity.FreeAccessType), null);
        }
    };

    handleCountryOfResidenceChange = (e: ISmartFieldChange): void => {
        if (e.bindingContext.getPath() === PrEmployeeEntity.CountryOfResidency) {
            const { storage } = this.props;
            storage.setValueByPath(createPath(PrEmployeeEntity.PermanentAddress, PrEmployeePermanentAddressEntity.Country), e.additionalData);

            if (e.value !== CountryCode.CzechRepublic) {
                storage.setValueByPath(createPath(PrEmployeeEntity.CzechAddress, PrEmployeeCzechAddressEntity.Country), CountryCode.CzechRepublic);
                storage.setValueByPath(createPath(PrEmployeeEntity.TaxResidenceAddress, PrEmployeePermanentAddressEntity.Country), e.additionalData);
            }
            storage.refresh();
        }
    };

    handlePermanentAddressCountryChange = (e: ISmartFieldChange): void => {
        if (e.bindingContext.getNavigationPath(true) === createPath(PrEmployeeEntity.PermanentAddress, PrEmployeePermanentAddressEntity.Country)) {
            const { storage } = this.props;
            storage.setValueByPath(createPath(PrEmployeeEntity.CzechAddress, PrEmployeeCzechAddressEntity.Country), CountryCode.CzechRepublic);
            storage.refresh();
        }
    };

    handleDisabilityChange = (e: ISmartFieldChange): void => {
        if (e.bindingContext.getPath() === PrEmployeeTemporalCurrentEntity.PrDisabilityType && !e.value) {
            const temporalBagBc = e.bindingContext.getParent();
            const isZtpPBc = temporalBagBc.navigate(PrEmployeeTemporalCurrentEntity.IsZtpP);
            const dateDisabilityFromBc = temporalBagBc.navigate(PrEmployeeTemporalCurrentEntity.DateDisabilityFrom);
            const dateDisabilityToBc = temporalBagBc.navigate(PrEmployeeTemporalCurrentEntity.DateDisabilityTo);
            this.props.storage.setValue(isZtpPBc, false);
            this.props.storage.clearValue(dateDisabilityFromBc);
            this.props.storage.clearValue(dateDisabilityToBc);

            handleCurrentTemporalPropChange({
                bindingContext: isZtpPBc,
                value: false
            }, this.props.storage);
            handleCurrentTemporalPropChange({
                bindingContext: dateDisabilityFromBc,
                value: null
            }, this.props.storage);
            handleCurrentTemporalPropChange({
                bindingContext: dateDisabilityToBc,
                value: null
            }, this.props.storage);
        }
    }

    setupBirthNumberProperLabel = () => {
        const { storage } = this.props;
        if (storage.data.bindingContext) {
            const country = storage.data.entity[PrEmployeeEntity.CitizenshipCode];
            const lblInfo = storage.getInfo(storage.data.bindingContext.navigate(PrEmployeeEntity.PersonalIdentificationNumber));
            if (lblInfo) {
                lblInfo.label = country === CountryCode.CzechRepublic ? i18next.t("Employee:Table.BirthNumber") : i18next.t("Employee:Form.CzechBirthNumber");
            }
        }
    }

    handleChange = (e: ISmartFieldChange): void => {
        this.props.storage.handleChange(e);
        this.handlePensionChange(e);
        handleCurrentTemporalPropChange(e, this.props.storage as FormStorage<any>);
        this.handleCitizenshipChange(e);
        this.handlePermanentAddressCountryChange(e);
        this.handleCountryOfResidenceChange(e);
        handleBankFieldChange({
            storage: this.props.storage as FormStorage<any>,
            isEntityWithoutBP: true,
            businessPartner: null,
            type: null
        }, e);
        this.handleDisabilityChange(e);

        // triggerAdditionalTasks === true for select like components has same
        // meaning as triggerAdditionalTasks === undefined for the rest types
        const shouldUpdate = e.triggerAdditionalTasks !== false;
        this.props.storage.refreshFields(shouldUpdate);
    };

    handleFieldStateChange(bc: BindingContext, state: ICachedFieldState, prevState: ICachedFieldState) {
        super.handleFieldStateChange(bc, state, prevState);

        if (state.isRequired !== prevState.isRequired &&
            bc.getPath() === AddressEntity.Street && bc.getParent().getPath() === PrEmployeeEntity.CzechAddress) {
            // Czech address field switch from required to not required -> fill or clear country code
            const { storage } = this.props;
            if (state.isRequired) {
                storage.setValueByPath(createPath(PrEmployeeEntity.CzechAddress, PrEmployeeCzechAddressEntity.Country), CountryCode.CzechRepublic);
            } else {
                storage.clearValueByPath(createPath(PrEmployeeEntity.CzechAddress, PrEmployeeCzechAddressEntity.Country));
            }
        }
    }

    handlePensionChange(e: ISmartFieldChange): void {
        const path = e.bindingContext.getPath(true);
        const { storage } = this.props;
        if (path === PrEmployeeTemporalEntity.PensionType) {
            const bagItemBc = e.bindingContext.getParent();
            if (e.value !== PrPensionTypeCode.OldAge) {
                storage.setValue(bagItemBc.navigate(applyPensionDiscountPath), false);
                storage.clearValue(bagItemBc.navigate(PrEmployeeTemporalEntity.DateWorkingRetireeSocialInsuranceDiscountFrom));
                storage.setValue(bagItemBc.navigate(PrEmployeeTemporalEntity.IsEarlyRetirement), false);
                storage.setValue(bagItemBc.navigate(PrEmployeeTemporalEntity.HasReducedRetirementAge), false);
            }
            storage.refreshFields();
        }

        if (path === applyPensionDiscountPath) {
            const bagItemBc = e.bindingContext.getParent();
            if (!e.value) {
                storage.clearValue(bagItemBc.navigate(PrEmployeeTemporalEntity.DateWorkingRetireeSocialInsuranceDiscountFrom));
            }
            storage.addActiveField(bagItemBc.navigate(PrEmployeeTemporalEntity.DateWorkingRetireeSocialInsuranceDiscountFrom));
            storage.refreshFields();
        }

        const pensionTypeBc = storage.data.bindingContext
            .navigate(PrEmployeeEntity.CurrentTemporalPropertyBag)
            .navigate(PrEmployeeTemporalCurrentEntity.PensionType);
        const pensionType = storage.getValue(pensionTypeBc, { useDirectValue: false });
        if (path === ShowPensionPath && pensionType) {
            setTemporalCurrentValue(storage as unknown as FormStorage, pensionTypeBc, pensionType);
        }
    }

    /** Take ranges of one temporal property group,
     * set DateValidTo to the day before today (today is considered as current range - it won't change the current
     * value so the switch would be enabled after save),
     * if current range exists and remove any planned items. */
    finishPropertyBeforeToday = (propName: string, entity: typeof this.entity): typeof this.entity => {
        const temporalPropertyBagBc = this.props.storage.data.bindingContext.navigate("TemporalPropertyBag");
        const bc = temporalPropertyBagBc.navigate(propName);
        const properties = getGroupingProperties(bc, this.props.storage.getThis());
        const temporalBag = this.props.storage.getValue(temporalPropertyBagBc, { dataStore: entity }) as TTemporal[] ?? [];
        let groupedRanges = groupAndOptimizeRanges(temporalBag, properties);

        if (!groupedRanges || groupedRanges.length === 0) {
            return entity;
        }

        const theDayBeforeToday = getUtcDayjs().subtract(1, "day");

        // remove all future planned items
        groupedRanges = groupedRanges.filter(range => theDayBeforeToday.isAfter(range.DateValidFrom));

        const currentRangeIndex = getCurrentRangeIndex(groupedRanges);
        const currentRange = groupedRanges[currentRangeIndex];

        if (currentRange) {
            // otherwise, finish the group today
            currentRange.DateValidTo = theDayBeforeToday.toDate();
        }

        const ungroupedRanges = ungroupTemporalRanges(groupedRanges, temporalBag, properties);

        return setBoundValue({
            bindingContext: temporalPropertyBagBc,
            dataBindingContext: bc.getRootParent(),
            data: entity,
            newValue: ungroupedRanges
        });
    };

    onBeforeSave(): IPrEmployeeEntityExtended {
        let data = cloneDeep(this.entity);

        const _isForeigner = isForeigner({ storage: this.props.storage });

        const years = this.props.storage.getValueByPath(PreviousEmploymentDurationYearsPath) ?? 0;
        const days = this.props.storage.getValueByPath(PreviousEmploymentDurationDaysPath) ?? 0;

        data.PreviousEmploymentDurationDays = years * 365 + days;

        if (!this.entity[ShowContactAddressPath]) {
            data.ContactAddress = null;
        }

        if (isAddressEmpty(data.CzechAddress) || (isVisibleByPath(this.props.storage, ShowCzechAddressPath) && !this.entity[ShowCzechAddressPath])) {
            // clear czech address in case it is not filled (invisible or not mandatory)
            data.CzechAddress = null;
        }

        if (hasCzechResidency({ storage: this.props.storage })) {
            data.TaxResidenceAddress = null;
        }

        // for hidden form groups,
        // end current ranges today and remove any planned values
        if (!this.entity[ShowDisabilityPath]) {
            data = this.finishPropertyBeforeToday(PrEmployeeTemporalEntity.PrDisabilityType, data);
        }

        if (!this.entity[ShowPensionPath]) {
            data = this.finishPropertyBeforeToday(PrEmployeeTemporalEntity.PensionType, data);
        }

        if (!this.entity[ShowSocialInsuranceDiscountLocalPath]) {
            data = this.finishPropertyBeforeToday(PrEmployeeTemporalEntity.PrSocialInsuranceDiscountType, data);
        }

        if (!this.entity[ShowStudyPath]) {
            data = this.finishPropertyBeforeToday(PrEmployeeTemporalEntity.PrFormOfStudyType, data);
        }

        if (!this.entity[ShowForeignInsuranceCarrierPath]) {
            data.ForeignInsuranceCarrier = null;
        }

        if (!data.SalaryCurrencyCode) {
            data.SalaryCurrencyCode = CurrencyCode.CzechKoruna;
        }

        if (!_isForeigner) {
            data.CzechAddress = null;
            data.LaborMarketAccess = null;
        }

        return data;
    }

    handleTaxPayerDeclarationClose = () => {
        this.props.storage.setCustomData({ taxPayerDeclarationDialogOpen: false });
        this.forceUpdate();
    };

    handleEmailToEmployeeWasSent = async (userEntity: IUserEntity) => {
        const { storage } = this.props;
        if (storage.getCustomData().sendEmployeeDialogType === SendToEmployeeActionType.SendEmployeeInvitation) {
            storage.setValueByPath(PrEmployeeEntity.User, userEntity);
            await storage.updateTabsVisibility([EMPLOYEE_USER_TAB_ID]);
            await storage.reload({ preserveInfos: true, preserveData: true, withoutBusy: true }); // reload to get new etag
        } else if (storage.getCustomData().sendEmployeeDialogType === SendToEmployeeActionType.SendPayslipPassword) {
            storage.setFormAlert({
                status: Status.Success,
                title: storage.t("Common:Validation.SuccessTitle"),
                subTitle: storage.t("Employee:Form.PayslipPasswordSent")
            });
        }
        this.handleSendToEmployeeDialogClose();
    };

    handleSendToEmployeeDialogClose = () => {
        this.props.storage.setCustomData({ sendEmployeeDialogType: null });
        this.forceUpdate();
    };


    handleSmartTemporalPropertyDialogChange(args: ISmartFieldChange): void {
        const { storage } = this.props;

        if (args.bindingContext.getPath() === PrEmployeeTemporalEntity.PrDisabilityType && !args.value) {
            const temporalBagBc = args.bindingContext.getParent();
            const isZtpPBc = temporalBagBc.navigate(PrEmployeeTemporalEntity.IsZtpP);
            storage.setValue(isZtpPBc, false);
            storage.clearValue(temporalBagBc.navigate(PrEmployeeTemporalEntity.DateDisabilityFrom));
            storage.clearValue(temporalBagBc.navigate(PrEmployeeTemporalEntity.DateDisabilityTo));
        }

        this.handlePensionChange(args);
        storage.refreshFields();
    }

    getCustomDialogArgs = memoizeOne(() => ({ isSingleEmployeeView: true }), () => []);

    renderTaxPayerDeclarationDialog = () => {
        const { storage } = this.props;
        const isOpen = storage.getCustomData().taxPayerDeclarationDialogOpen;

        if (!isOpen) {
            return null;
        }

        return <Dialog
            isEditableWindow={true}
            onConfirm={null}
            onClose={this.handleTaxPayerDeclarationClose}>
            <DialogPage
                formProps={{
                    isSimple: false,
                    shouldHideVariant: false,
                    defaultData: {
                        Employee: storage.data.entity
                    }
                }}
                hasPreview
                getDefArgs={this.getCustomDialogArgs()}
                rootStorage={storage}
                tableViewProps={this.getCustomDialogArgs()}
                tableView={TaxpayerDeclarationTableView as any}
                getDef={getTaxpayerDeclarationDefinitions}/>
        </Dialog>;
    }

    get submissionDefaultData(): IEntity {
        const { storage } = this.props;
        const {
            FirstName,
            LastName,
            PersonalIdentificationNumber,
            DateOfBirth,
            EuSocialInsuranceNumber,
            Id
        } = storage.data.entity;
        return {
            Employee: {
                Employee: {
                    Id, FirstName, LastName
                },
                PersonalIdentificationNumber,
                DateOfBirth,
                EuSocialInsuranceNumber,
                FirstName,
                LastName
            }
        };
    }

    get submissionSubtype(): PrSubmissionSubtypeCode {
        return null;
    }

    /* Shared code between EmployeeFormView, SocialEventFormView and EmploymentFormView */
    closeSubmissionDialog = (): void => {
        this.props.storage.setCustomData({ submissionType: null });
        this.forceUpdate();
    };

    confirmSubmissionDialog = (): void => {
        const { storage } = this.props;
        // Submission saved -> reload the form
        // todo: reload only the table?
        storage.reload({ preserveInfos: true, preserveData: true, withoutBusy: true });
        // close the dialog
        this.closeSubmissionDialog();
    };

    getSubmissionSummaryItems(): ISummaryItemProps[] {
        const def = this.props.storage.data.definition;
        const employeeSummaryItem = def.summary.find(item => item.id === employeeSummaryLocalPath);
        const userAvatar = employeeSummaryItem?.formatter(null, { storage: this.props.storage });

        return [{
            id: employeeSummaryLocalPath,
            label: null, // wo employee
            value: userAvatar
        }];
    }

    renderSubmissionDialog = () => {
        const { storage } = this.props;
        const { submissionType } = storage.getCustomData();
        if (submissionType) {
            return (
                <SubmissionWizard generatorKey={storage.data.bindingContext?.getKey()}
                                  onCancel={this.closeSubmissionDialog}
                                  onConfirm={this.confirmSubmissionDialog}
                                  defaultData={this.submissionDefaultData}
                                  summaryItems={this.getSubmissionSummaryItems()}
                                  mainType={submissionType}
                                  subType={this.submissionSubtype}/>
            );
        }
        return null;
    }
    /* /end/ Shared code between EmployeeFormView, SocialEventFormView and EmploymentFormView */

    renderInvitationDialog = () => {
        const { storage } = this.props;
        const employeeDialogType = storage.getCustomData().sendEmployeeDialogType;

        if (!isDefined(employeeDialogType)) {
            return null;
        }

        const title = employeeDialogType === SendToEmployeeActionType.SendEmployeeInvitation
            ? storage.t("Employee:Form.InviteToEmployeePortal")
            : storage.t("Employee:Form.SendPayslipPassword");

        const confirmText = employeeDialogType === SendToEmployeeActionType.SendEmployeeInvitation
            ? storage.t("Employee:Form.Invite")
            : storage.t("Employee:Form.Send");

        return <PlugAndPlayForm
            getDef={sendInvitationDef}
            t={storage.t}
            bindingContext={storage.data.bindingContext}
            initialData={{
                entity: {
                    [sendToEmployeeMailPath]: this.entity.Email
                }
            }}
            formView={SentToEmployeeFormView}
            formViewProps={{
                dialogProps: {
                    title
                },
                formProps: {
                    renderScrollbar: false,
                    isSimple: true
                },
                confirmText,
                onAfterConfirm: this.handleEmailToEmployeeWasSent,
                onClose: this.handleSendToEmployeeDialogClose
            }}
            customFormViewProps={{
                employeeDialogType
            }}
        />;
    };

    renderCustomDialogs() {
        return (<>
            {super.renderCustomDialogs()}
            {this.renderSubmissionDialog()}
            {this.renderTaxPayerDeclarationDialog()}
            {this.renderInvitationDialog()}
        </>);
    }
}

export default withPermissionContext(EmployeeFormView);
